import { Button, Stack, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import { useTranslations } from '@/i18n/use-translations';
import { MediaControlSettingsForm } from '@/components/playout/media-control-settings-form';
import type { ResolvedMediaControlSettings } from '@/types/playout/media-control-settings.types';
import { usePatchEventMediaControlSettingsMutation } from '@/state/mutations/playout/use-patch-media-control-settings';
import { useNotifications } from '@/hooks/use-notifications';
import { useAuth } from '@clerk/clerk-react';
import { METHOD, sendJSONRequest } from '@/state/http-request.helpers';

export function EventPlayoutSettingsPanel({ eventId, multiFlow }: { eventId?: string; multiFlow?: boolean }) {
  const t = useTranslations();
  const notification = useNotifications();
  const { getToken } = useAuth();
  const { mutateAsync, isPending } = usePatchEventMediaControlSettingsMutation(eventId);
  const [settings, setSettings] = useState<ResolvedMediaControlSettings | null>(null);

  useEffect(() => {
    if (!eventId || !multiFlow) return;

    let cancelled = false;

    void (async () => {
      try {
        const token = await getToken();
        const url = `${import.meta.env.VITE_API_URL}/events/${eventId}/settings/playout/media-control`;
        const data = (await sendJSONRequest({
          url,
          method: METHOD.GET,
          token: token ?? '',
        })) as ResolvedMediaControlSettings;
        if (!cancelled) {
          setSettings(data);
        }
      } catch {
        if (!cancelled) {
          notification.notifyError(
            t('playout.mediaControl.loadError', { defaultValue: 'Could not load media control settings' }),
          );
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [eventId, multiFlow, getToken, notification, t]);

  if (!multiFlow) {
    return (
      <Typography variant="body2" color="text.secondary">
        {t('playout.mediaControl.multiFlowOnly', {
          defaultValue: 'Playout media control settings are available for multi-flow events only.',
        })}
      </Typography>
    );
  }

  if (!settings) {
    return null;
  }

  const handleSave = async () => {
    try {
      const updated = await mutateAsync({
        mediaControl: {
          bindings: settings.bindings,
          allowFlowOverride: settings.allowFlowOverride,
        },
      });
      setSettings(updated);
      notification.notifySuccess(
        t('playout.mediaControl.saveSuccess', { defaultValue: 'Media control settings saved' }),
      );
    } catch {
      notification.notifyError(
        t('playout.mediaControl.saveError', { defaultValue: 'Could not save media control settings' }),
      );
    }
  };

  return (
    <Stack spacing={2}>
      <Typography variant="subtitle2">
        {t('playout.mediaControl.sectionTitle', { defaultValue: 'Media control' })}
      </Typography>
      <MediaControlSettingsForm
        settings={settings}
        disabled={!settings.canEditAtEvent || isPending}
        showAllowFlowOverride
        onBindingsChange={(bindings) => setSettings((prev) => (prev ? { ...prev, bindings } : prev))}
        onAllowFlowOverrideChange={(allowFlowOverride) =>
          setSettings((prev) => (prev ? { ...prev, allowFlowOverride } : prev))
        }
      />
      <Button variant="contained" onClick={() => void handleSave()} disabled={!settings.canEditAtEvent || isPending}>
        {t('common.save')}
      </Button>
    </Stack>
  );
}
